import { ErrorMessage, Field } from "formik";
import { IMAGE_FETCH_URL, IMAGE_URL } from "../../config";
import { useState, useRef } from "react";
import get from "lodash/get";
import set from "lodash/set";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FormDocumentAltField = ({
  label = "Upload",
  name = "imageField",
  className = "",
  ...props
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const inputRef = useRef(null);

  const uploadFile = (file, form, name) => {
    if (!file) return;

    setPreviewFile(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", IMAGE_URL);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(percent);
      }
    };

    xhr.onloadstart = () => {
      setIsUploading(true);
      setUploadProgress(0);
    };

    xhr.onload = () => {
      setIsUploading(false);
      setPreviewFile(null);

      if (xhr.status === 200) {
        const response = JSON.parse(xhr.responseText);
        const filePath = response?.data?.filePath;

        const fullUrl = filePath.startsWith("http")
          ? filePath
          : `${IMAGE_FETCH_URL}${filePath}`;

        const updatedValues = { ...form.values };
        set(updatedValues, name, { url: fullUrl, alt: `${file.name}` });
        form.setValues(updatedValues);
      } else {
        alert("Image upload failed!");
      }
    };

    xhr.onerror = () => {
      setIsUploading(false);
      setPreviewFile(null);
      alert("An error occurred during upload!");
    };

    xhr.send(formData);
  };

  const handleFileSelect = (event, form, name) => {
    const file = event.target.files[0];
    uploadFile(file, form, name);
  };

  const handleDrop = (event, form, name) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files[0];
    uploadFile(file, form, name);
  };

  const handleRemoveImage = (form, name) => {
    const updatedValues = { ...form.values };
    set(updatedValues, name, "");
    form.setValues(updatedValues);
  };

  return (
    <Field name={name}>
      {({ form, meta }) => {
        const file = get(form.values, name);
        const error = meta.touched && meta.error;

        return (
          <div className={`flex flex-col gap-2 w-full ${className}`}>
            <label className="font-medium text-gray-800">
              {formatUnderscoreToSpace(label)}
            </label>

            {!file ? (
              <div
                className={`border-2 rounded-lg p-6 text-center transition-all w-[250px] h-[250px] relative overflow-hidden 
               ${
                 meta.touched && meta.error
                   ? "border-errorColor"
                   : isDragActive
                     ? "border-primary bg-purple-50"
                     : "border-gray-300 bg-gray-50 hover:border-primary"
               }
               }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragActive(true);
                }}
                onDragLeave={() => setIsDragActive(false)}
                onDrop={(e) => handleDrop(e, form, name)}
              >
                {isUploading && previewFile && (
                  <img
                    src={previewFile}
                    alt="uploading"
                    className="absolute inset-0 w-full h-full object-cover scale-105 blur-[3px]"
                  />
                )}

                {isUploading ? (
                  <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-white px-3">
                    <p className="text-sm mb-2 font-medium">Uploading...</p>

                    <div className="w-full max-w-[180px] bg-white/40 rounded-full h-[6px] overflow-hidden shadow">
                      <div
                        className="h-full rounded-full transition-all duration-200"
                        style={{
                          width: `${uploadProgress}%`,
                          background:
                            "linear-gradient(to right, #00e676, #1e9d43)",
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col justify-center items-center relative z-10 w-full h-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-8 h-8 text-gray-400"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 
                              2.25 0 0021 18.75V16.5m-3-6l-6-6m0 
                              0l-6 6m6-6V15"
                      />
                    </svg>
                    <p className="text-gray-600 mt-2 text-fs_14">
                      Drag & drop image or{" "}
                      <span
                        onClick={() => inputRef.current.click()}
                        className="text-primary font-medium cursor-pointer"
                      >
                        Browse
                      </span>
                    </p>
                  </div>
                )}

                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*,video/mp4"
                  className="hidden"
                  onChange={(event) => handleFileSelect(event, form, name)}
                />
              </div>
            ) : (
              <div>
                <div className="relative w-[250px] h-[250px]">
                  <img
                    src={file.url}
                    alt="Uploaded"
                    className="w-full h-full rounded-lg object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(form, name)}
                    className="absolute top-2 right-2 bg-black/60 text-white text-sm px-2 py-1 rounded-full"
                  >
                    ✕
                  </button>
                </div>
                <div className={`flex flex-col gap-1 col-span-3 ${className}`}>
                  <label
                    htmlFor={name + "_alt"}
                    className={`text-fs_14 md:text-fs_16 font-fw_400 capitalize ${name + "_alt"}`}
                  >
                    {formatUnderscoreToSpace(name + "_alt")}
                  </label>
                  <input
                    id={name + "_alt"}
                    name={name + "_alt"}
                    value={file.alt}
                    onChange={(e) => {
                      const updatedValues = {
                        ...form.values,
                      };
                      set(updatedValues, name, {
                        ...file,
                        alt: e.target.value,
                      });
                      form.setValues(updatedValues);
                    }}
                    className={`px-3 py-2 w-full rounded-lg lg:text-fs_16 border-[1px]
                                    text-fs_14 text-darkText placeholder-greyText placeholder:text-fs_14 md:placeholder:text-fs_16 focus:outline-none border-greyText`}
                    required
                  />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <span className="text-red-500 text-sm">
                <ErrorMessage name={name} />
              </span>
            )}
          </div>
        );
      }}
    </Field>
  );
};
